import { type ApolloClient, gql } from '@apollo/client/core';
import { Mutation } from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { take } from '../../../util/takePayload';
import { forAccount } from '../../accountContext';

export const CREATE_DIRECT_UPLOAD_URL_MUTATION = gql`
  mutation CreateDirectUploadUrl($input: CreateDirectUploadUrlMutationInput!) {
    createDirectUploadUrl(input: $input) {
      attachmentId
      presignedUrl
      errors {
        message
        path
      }
    }
  }
`;

export const CONFIRM_ATTACHMENT_UPLOAD_MUTATION = gql`
  mutation ConfirmAttachmentUpload($input: ConfirmAttachmentUploadMutationInput!) {
    confirmAttachmentUpload(input: $input) {
      success
      errors {
        message
        path
      }
    }
  }
`;

type DirectUploadUrl = {
  attachmentId: number;
  presignedUrl: string;
};

/**
 * `accountId` names the account the attachment is built on, for callers that are not operating in
 * their own: the VenuePilot-staff Clients screens administer an account their operator is not a
 * member of, so without it the row is created under the operator's own account and the save that
 * follows is refused by `Account#logo_belongs_to_account`. Omitted, the request is scoped by the
 * connection store as usual.
 */
export const createDirectUploadUrl = async (
  client: ApolloClient<Mutation>,
  input: { name: string; checksum: string },
  accountId?: number,
): Promise<VPResult<DirectUploadUrl, VPErrors.VPError[]>> => {
  const { data } = await client.mutate({
    mutation: CREATE_DIRECT_UPLOAD_URL_MUTATION,
    variables: { input },
    context: accountId ? forAccount(accountId) : undefined,
  });

  const result = data?.createDirectUploadUrl;

  const hasDirectUploadUrl = result?.attachmentId != null && !!result.presignedUrl;
  if (result && hasDirectUploadUrl) {
    return {
      success: true,
      data: take<DirectUploadUrl>(result, 'attachmentId', 'presignedUrl') as DirectUploadUrl,
    };
  }

  return {
    success: false,
    errors: VPErrors.fromGqlCollection(result?.errors),
  };
};

/** `accountId` as in `createDirectUploadUrl`: the confirm has to land in the same account. */
export const confirmAttachmentUpload = async (
  client: ApolloClient<Mutation>,
  attachmentId: number,
  accountId?: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  const { data } = await client.mutate({
    mutation: CONFIRM_ATTACHMENT_UPLOAD_MUTATION,
    variables: { input: { attachmentId } },
    context: accountId ? forAccount(accountId) : undefined,
  });

  const result = data?.confirmAttachmentUpload;

  if (result?.success) {
    return { success: true, data: null };
  }

  return {
    success: false,
    errors: VPErrors.fromGqlCollection(result?.errors),
  };
};
